/**
 * Hook pour gérer la réinitialisation automatique de la langue
 * quand le support multilingue est désactivé
 *
 * Ce hook surveille la configuration multilingue de l'entreprise et force
 * automatiquement le retour à la langue par défaut lorsque le support
 * multilingue est désactivé, tout en nettoyant les préférences de langue
 * stockées dans localStorage.
 *
 * @module hooks/useLanguageReset
 * @author Équipe Brydge
 * @since 1.0.0
 */

import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';

/** Clé localStorage pour la langue sélectionnée dans les widgets */
const WIDGET_LANGUAGE_KEY = 'brydge_widget_language';
/** Clé localStorage par défaut de i18next */
const I18N_LANGUAGE_KEY = 'i18nextLng';

/**
 * Hook de réinitialisation automatique de la langue.
 *
 * Force le retour à la langue par défaut dans deux cas :
 * - Le support multilingue est entièrement désactivé
 * - La langue courante (stockée en localStorage) a été retirée des langues actives
 *
 * @param {Object} companyDetails - Détails de l'entreprise contenant la configuration multilingue
 * @param {Object} companyDetails.multiLanguage - Configuration multilingue
 * @param {boolean} companyDetails.multiLanguage.enabled - Support multilingue activé/désactivé
 * @param {string} companyDetails.multiLanguage.defaultLanguage - Langue par défaut (fr, en, nl, de)
 * @param {string[]} companyDetails.multiLanguage.enabledLanguages - Langues activées
 * @return {void} Ce hook ne retourne rien, il agit par effet de bord
 *
 * @example
 * function GiftVouchersListView() {
 *   const {companyDetails} = useGetCompanyDetails(slug);
 *
 *   // Réinitialise automatiquement la langue si multilingue désactivé
 *   useLanguageReset(companyDetails);
 *
 *   return <div>...</div>;
 * }
 */
export function useLanguageReset(companyDetails) {
  const {i18n} = useTranslation();

  useEffect(() => {
    if (companyDetails) {
      const multiLanguageConfig = companyDetails.multiLanguage;

      // Ne rien faire si la config multiLanguage n'est pas définie (backward compatibility)
      if (!multiLanguageConfig) return;

      const isMultiLanguageEnabled = multiLanguageConfig.enabled || false;
      const defaultLanguage = multiLanguageConfig.defaultLanguage || 'fr';
      const enabledLanguages = multiLanguageConfig.enabledLanguages || ['fr'];
      const currentLanguage = i18n.language;

      // Langue courante absente de la liste des langues actives (feature désactivée ou langue retirée)
      const isCurrentLanguageUnavailable = !isMultiLanguageEnabled ||
        !enabledLanguages.includes(currentLanguage);

      if (isCurrentLanguageUnavailable && currentLanguage !== defaultLanguage) {
        // Nettoyer les préférences de langue stockées
        try {
          localStorage.removeItem(WIDGET_LANGUAGE_KEY);
          localStorage.removeItem(I18N_LANGUAGE_KEY);
        } catch {
          // localStorage indisponible (ex: navigation privée)
        }

        // Forcer le retour à la langue par défaut
        i18n.changeLanguage(defaultLanguage);
      }
    }
  }, [companyDetails, i18n]);
}

